import React, { useState, useRef } from "react";
import SEO from "../components/SEO";
import { franchisePageSchema } from "../data/schemas";
import FAQSection from "../components/FAQSection";
import { franchiseFaqs } from "../data/faqs";
import ReCaptcha from "../components/ReCaptcha";
import { sendFranchiseEmail } from "../services/emailService";

/* ─── Radio Group (fixed click handler) ──────────────────────── */
const RadioGroup = ({ options, value, onChange }) => (
  <div className="flex flex-wrap gap-3">
    {options.map((opt) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-all duration-200 select-none text-sm font-medium ${
          value === opt
            ? "border-[#9b1c2e] bg-[#fdf0f2] text-[#9b1c2e]"
            : "border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50"
        }`}
      >
        <span
          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
            value === opt ? "border-[#9b1c2e]" : "border-gray-300"
          }`}
        >
          {value === opt && (
            <span className="w-2 h-2 rounded-full bg-[#9b1c2e]" />
          )}
        </span>
        {opt}
      </button>
    ))}
  </div>
);

/* ─── Field Wrapper ───────────────────────────────────────────── */
const Field = ({ label, required, children }) => (
  <div className="space-y-2">
    <label className="block text-sm font-semibold text-gray-700">
      {label}
      {required && <span className="text-[#9b1c2e] ml-1">*</span>}
    </label>
    {children}
  </div>
);

const inputClass =
  "w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-[#9b1c2e] focus:ring-2 focus:ring-[#9b1c2e]/10 text-gray-800 text-sm bg-white transition-all duration-200 placeholder-gray-400";

/* ─── Success Modal ───────────────────────────────────────────── */
const SuccessModal = ({ name, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
    {/* Backdrop */}
    <div
      className="absolute inset-0 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    />
    {/* Card */}
    <div className="relative bg-white rounded-3xl shadow-2xl p-10 max-w-md w-full text-center z-10 animate-fade-in">
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors"
      >
        <svg
          className="w-4 h-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      {/* Icon */}
      <div className="w-20 h-20 bg-[#9b1c2e]/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg
          className="w-10 h-10 text-[#9b1c2e]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 13l4 4L19 7"
          />
        </svg>
      </div>

      <h2
        className="text-2xl font-bold text-gray-900 mb-3"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        Application Submitted!
      </h2>
      <p className="text-gray-500 text-sm leading-relaxed mb-8">
        Thank you, <strong className="text-gray-800">{name}</strong>! We've
        received your franchise application. Our team will review it and contact
        you within 2–3 business days.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <a
          href="tel:+919087222264"
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#9b1c2e] hover:bg-[#7d1624] text-white text-sm font-semibold rounded-full transition-colors"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
            />
          </svg>
          Call Us Now
        </a>
        <button
          onClick={onClose}
          className="px-6 py-3 border border-gray-200 hover:border-gray-300 text-gray-600 text-sm font-semibold rounded-full transition-colors"
        >
          Close
        </button>
      </div>
    </div>
  </div>
);

/* ─── Section Header ──────────────────────────────────────────── */
const SectionNum = ({ num, label }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="w-7 h-7 rounded-full bg-[#9b1c2e] flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
      {num}
    </div>
    <h3 className="text-base font-bold text-gray-900 uppercase tracking-wide">
      {label}
    </h3>
  </div>
);

/* ─── Main Page ───────────────────────────────────────────────── */
const FranchisePage = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    mobile: "",
    occupation: "",
    location: "",
    experience: "",
    role: "",
    propertyStatus: "",
    locationType: "",
    launchDate: "",
    message: "",
  });
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const captchaRef = useRef(null);

  /* Text/email/tel/textarea handler */
  const setField = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  /* Radio handler — receives the value string directly */
  const setRadio = (key) => (val) => setForm((f) => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) {
      setCaptchaError(
        "Please complete the reCAPTCHA verification before submitting your franchise application.",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      await sendFranchiseEmail(form, captchaToken);
      setShowModal(true);
    } catch (err) {
      console.error("Failed to send franchise application:", err);
      setCaptchaError(
        "Failed to submit application. Please try again or contact us directly.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      name: "",
      email: "",
      mobile: "",
      occupation: "",
      location: "",
      experience: "",
      role: "",
      propertyStatus: "",
      locationType: "",
      launchDate: "",
      message: "",
    });
    setCaptchaToken("");
    setCaptchaError("");
    captchaRef.current?.reset();
    setShowModal(false);
  };

  return (
    <div
      className="min-h-screen bg-[#f9f5f0]"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      <SEO
        title="Barbequeen Franchise | Start Restaurant Business in India"
        metaTitle="Barbequeen Franchise | Start Restaurant Business in India"
        description="Apply for a Barbequeen franchise and bring Salem's signature taste to your city. Proven business model, full operational support, and ongoing guidance."
        keywords="Barbequeen franchise, restaurant franchise opportunity, start food franchise Tamil Nadu, BBQ franchise India, food business investment, Salem restaurant franchise"
        robots="index, follow"
        canonical="https://barbequeen.com/franchise"
        og={{
          type: "website",
          url: "https://barbequeen.com/franchise",
          title: "Barbequeen Franchise | Partner With Us",
          description:
            "Join the Barbequeen family and bring Salem's signature taste to your city. Complete operational support and proven business model.",
          image: "https://barbequeen.com/images/franchise-banner.jpg",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN",
        }}
        geo={{
          region: "IN-TN",
          placename: "Salem, Tamil Nadu, India",
        }}
        schema={franchisePageSchema}
      />
      {/* ── Success Modal ── */}
      {showModal && <SuccessModal name={form.name} onClose={resetForm} />}

      {/* ── Hero Banner ── */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: "340px" }}
      >
        {/* Background food/restaurant image */}
        <img
          src="https://barbequeen.com/public/uploads/04-14-2023-1681461467.jpg"
          alt="Partner With Us"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        {/* Dark red-tinted overlay */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,0,2,0.72) 0%, rgba(10,0,2,0.82) 100%)",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 py-28 text-center">
          <p className="text-xs font-bold tracking-[0.5em] uppercase text-[#c0424e] mb-4">
            Partner With Us
          </p>
          <h1
            className="text-4xl sm:text-6xl font-bold text-white uppercase leading-tight tracking-wide mb-5"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Franchise
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto leading-relaxed">
            Join the Barbequeen family and bring Salem's signature taste to your
            city.
          </p>
        </div>
      </section>

      {/* ── Form + Sidebar ── */}
      <section className="max-w-6xl mx-auto px-4 lg:px-8 py-16 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* ── Form Column ── */}
          <div className="lg:col-span-8">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Form Header */}
              <div className="bg-gradient-to-r from-[#0e0003] to-[#2a0008] px-8 sm:px-12 py-10">
                <p className="text-xs font-bold tracking-[0.4em] uppercase text-[#c0424e] mb-2">
                  Apply Now
                </p>
                <h2
                  className="text-2xl sm:text-3xl font-bold text-white"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Franchise Application Form
                </h2>
                <p className="text-gray-400 text-sm mt-2">
                  All fields marked <span className="text-[#c0424e]">*</span>{" "}
                  are required.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="px-8 sm:px-12 py-10 space-y-10"
              >
                {/* Section 1 — Basic Info */}
                <div>
                  <SectionNum num="1" label="Basic Information" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <Field label="Your Name" required>
                      <input
                        type="text"
                        placeholder="Enter your full name"
                        value={form.name}
                        onChange={setField("name")}
                        required
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Email ID" required>
                      <input
                        type="email"
                        placeholder="you@example.com"
                        value={form.email}
                        onChange={setField("email")}
                        required
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Mobile Number" required>
                      <input
                        type="tel"
                        placeholder="+91 XXXXX XXXXX"
                        value={form.mobile}
                        onChange={setField("mobile")}
                        required
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Current Occupation / Business" required>
                      <input
                        type="text"
                        placeholder="e.g. Business Owner, Manager…"
                        value={form.occupation}
                        onChange={setField("occupation")}
                        required
                        className={inputClass}
                      />
                    </Field>
                    <Field label="Preferred Location" required>
                      <input
                        type="text"
                        placeholder="City / Area you prefer"
                        value={form.location}
                        onChange={setField("location")}
                        required
                        className={inputClass}
                      />
                    </Field>
                  </div>
                </div>

                <div className="border-t border-gray-100" />

                {/* Section 2 — Experience & Role */}
                <div>
                  <SectionNum num="2" label="Experience & Role" />
                  <div className="space-y-6">
                    <Field label="Food Industry Experience" required>
                      <RadioGroup
                        options={["YES", "NO"]}
                        value={form.experience}
                        onChange={setRadio("experience")}
                      />
                    </Field>
                    <Field label="Preferred Role" required>
                      <RadioGroup
                        options={["Active", "Part-time", "Investor"]}
                        value={form.role}
                        onChange={setRadio("role")}
                      />
                    </Field>
                  </div>
                </div>

                <div className="border-t border-gray-100" />

                {/* Section 3 — Property & Location */}
                <div>
                  <SectionNum num="3" label="Property & Location" />
                  <div className="space-y-6">
                    <Field label="Property Status" required>
                      <RadioGroup
                        options={["Owned", "Rented", "Searching"]}
                        value={form.propertyStatus}
                        onChange={setRadio("propertyStatus")}
                      />
                    </Field>
                    <Field label="Location Type" required>
                      <RadioGroup
                        options={[
                          "High-street",
                          "Mall",
                          "Stand-alone",
                          "Food court",
                          "Other",
                        ]}
                        value={form.locationType}
                        onChange={setRadio("locationType")}
                      />
                    </Field>
                  </div>
                </div>

                <div className="border-t border-gray-100" />

                {/* Section 4 — Timeline & Message */}
                <div>
                  <SectionNum num="4" label="Timeline & Message" />
                  <div className="space-y-6">
                    <Field label="Target Launch Date" required>
                      <RadioGroup
                        options={[
                          "Immediately",
                          "< 3 months",
                          "3–6 months",
                          "Later",
                          "Other",
                        ]}
                        value={form.launchDate}
                        onChange={setRadio("launchDate")}
                      />
                    </Field>
                    <Field label="Enter Your Message">
                      <textarea
                        rows={5}
                        placeholder="Tell us more about your plans, questions, or anything else you'd like us to know…"
                        value={form.message}
                        onChange={setField("message")}
                        className={`${inputClass} resize-none`}
                      />
                    </Field>
                  </div>
                </div>

                {/* Google reCAPTCHA */}
                <div className="pt-2">
                  <ReCaptcha
                    ref={captchaRef}
                    theme="light"
                    onChange={(token) => {
                      setCaptchaToken(token);
                      if (token) setCaptchaError("");
                    }}
                    onExpired={() => setCaptchaToken("")}
                    errorMessage={captchaError}
                  />
                </div>

                {/* Submit */}
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-12 py-4 bg-[#9b1c2e] hover:bg-[#7d1624] disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-bold tracking-widest uppercase rounded-xl transition-colors duration-200 inline-flex items-center justify-center gap-3 shadow-lg shadow-[#9b1c2e]/20"
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="w-4 h-4 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth={4}
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                        Submitting Application…
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={2.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                          />
                        </svg>
                        Submit Application
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-400 mt-3">
                    By submitting, you agree that Barbequeen may contact you
                    regarding your franchise enquiry.
                  </p>
                </div>
              </form>
            </div>
          </div>

          {/* ── Quick Contact Sidebar ── */}
          <aside className="lg:col-span-4 lg:sticky lg:top-6 space-y-5">
            {/* Contact Card */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="bg-gradient-to-r from-[#0e0003] to-[#2a0008] px-6 py-5">
                <p className="text-[10px] font-bold tracking-[0.4em] uppercase text-[#c0424e] mb-1">
                  Get in Touch
                </p>
                <h3
                  className="text-lg font-bold text-white"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Quick Contact
                </h3>
              </div>

              <div className="px-6 py-6 space-y-6">
                {/* Phone */}
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-[#9b1c2e]/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#9b1c2e]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.948V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold tracking-widest uppercase text-gray-400 mb-1">
                      For Franchise Enquiry
                    </p>
                    <a
                      href="tel:+919087222264"
                      className="text-base font-bold text-gray-900 hover:text-[#9b1c2e] transition-colors"
                    >
                      +91-9087222264
                    </a>
                  </div>
                </div>

                <div className="border-t border-gray-100" />

                {/* Location */}
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-[#9b1c2e]/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#9b1c2e]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold tracking-widest uppercase text-gray-400 mb-1">
                      Location
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      Nonvee Foods Private Limited,
                      <br />
                      # 32/32-1, Valmiki Street,
                      <br />
                      Salem – 636 005.
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-100" />

                {/* Email */}
                <div className="flex gap-4 items-start">
                  <div className="w-10 h-10 rounded-xl bg-[#9b1c2e]/10 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-[#9b1c2e]"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold tracking-widest uppercase text-gray-400 mb-1">
                      Email Id
                    </p>
                    <a
                      href="mailto:franchise.barbequeen@nonveefoods.com"
                      className="text-sm font-semibold text-[#9b1c2e] hover:underline break-all leading-relaxed"
                    >
                      franchise.barbequeen@
                      <br />
                      nonveefoods.com
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Office Hours */}
            <div className="bg-[#fdf0f2] border border-[#9b1c2e]/20 rounded-2xl px-6 py-5">
              <div className="flex gap-3 items-start">
                <svg
                  className="w-5 h-5 text-[#9b1c2e] flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-bold text-gray-800 mb-0.5">
                    Office Hours
                  </p>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Mon – Sat: 10:00 AM – 6:00 PM
                    <br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <FAQSection faqs={franchiseFaqs} />
    </div>
  );
};

export default FranchisePage;
